import { motion } from 'framer-motion'
import React, { useCallback, useEffect } from 'react'
import { FieldValues, useFormContext } from 'react-hook-form'
import { Button, TextArea } from '@demandlocal/components'
import { useAtom } from 'jotai'
import { createCampaignFormValuesAtom } from '../../InventoryCampaign/NewCampaign/CreateNewCampaignForm'
import { ReactComponent as PeopleIcon } from '../../../images/icons/audience_people_round.svg'
import { ReactComponent as AnnouncementIcon } from '../../../images/icons/announcement_round.svg'
import { ReactComponent as DollarSign } from '../../../images/icons/dollar_sign.svg'
import { take } from 'lodash'
import { ReactComponent as AwarenessIcon } from '../../../images/icons/awareness_icon.svg'
import { ReactComponent as ConsiderationIcon } from '../../../images/icons/consideration_icon.svg'
import { ReactComponent as ConversionIcon } from '../../../images/icons/conversion_icon.svg'
import { formatNumber } from '../../../lib/utils/formatNumber'
import dayjs from 'dayjs'
import { useEditLinkCampaign } from '../../../api/dealership/campaigns/useEditLinkCampaign'
import HoverMedia from '../../../components/HoverMedia'
import CampaignProgressBar from '../../../components/CampaignProgressBars/CampaignProgressBar'
import classNames from 'classnames'

type Props = {
  step: number
  setStep: (step: number) => void
}

const CreateNonInventoryCampaignReview = ({ step, setStep }: Props): JSX.Element => {
  const formContext = useFormContext()

  const [containerHeight, setContainerHeight] = React.useState(0)
  const [formValues, setFormValues] = useAtom(createCampaignFormValuesAtom)
  const { handleSubmit, setValue } = formContext
  const { mutateAsync: saveLinkCampaign, isLoading } = useEditLinkCampaign()

  useEffect(() => {
    if (formValues.campaign_name) setValue('campaign_name', formValues.campaign_name)
    if (formValues.notes) setValue('notes', formValues.notes)
  }, [setValue, formValues])

  const onSubmit = useCallback(
    async (values: Partial<FieldValues>): Promise<void> => {
      setFormValues((prevFormValues) => {
        return {
          ...prevFormValues,
          campaign_name: values.campaign_name,
          notes: values?.notes,
        }
      })

      if (formValues.campaign_id) {
        await saveLinkCampaign({
          data: {
            notes: values?.notes ?? null,
          },
          link_campaign_id: formValues.campaign_id,
        })
      }

      setStep(step + 1)
    },
    [formValues.campaign_id, saveLinkCampaign, setFormValues, setStep, step],
  )

  const submitOnEnter = useCallback(
    (e): void => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSubmit(onSubmit)()
      }
    },
    [handleSubmit, onSubmit],
  )

  useEffect(() => {
    document.addEventListener('keydown', submitOnEnter)
    return () => document.removeEventListener('keydown', submitOnEnter)
  }, [submitOnEnter])

  return (
    <motion.form
      onSubmit={handleSubmit(onSubmit)}
      className={'flex flex-1 flex-col overflow-auto pb-20'}>
      <motion.div
        ref={(_ref) => {
          if (containerHeight === 0 && _ref?.clientHeight)
            setContainerHeight(_ref?.clientHeight ?? 0)
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={classNames('flex flex-1 flex-col pt-8 pb-8 overflow-auto', {
          'max-h-[auto]': containerHeight === 0,
          'max-h-full]': containerHeight !== 0,
        })}>
        {containerHeight > 0 && (
          <div className={'flex flex-col px-12'}>
            <span className={'font-bold'}>Your new campaign consists of:</span>
            <div className={'w-full py-8 border-b-2'}>
              <div className={'flex flex-row gap-3 items-center justify-between'}>
                <div className={'flex flex-row items-center gap-3'}>
                  <PeopleIcon />
                  <span className={'font-bold'}>Campaign Objective</span>
                </div>
              </div>

              <div
                className={
                  'my-4 border-2 p-4 flex flex-row items-center  rounded-lg w-fit'
                }>
                {formValues?.objective === 'awareness' ? (
                  <>
                    <span className={'object-scale-down h-fit'}>
                      <AwarenessIcon />
                    </span>
                    <div className={'flex flex-col gap-3'}>
                      <span className={'font-bold'}>Awareness</span>
                      <span>Get people to know your brand</span>
                    </div>
                  </>
                ) : formValues?.objective === 'consideration' ? (
                  <>
                    <span className={'object-scale-down h-fit'}>
                      <ConsiderationIcon />
                    </span>
                    <div className={'flex flex-col gap-3'}>
                      <span className={'font-bold'}>Consideration</span>
                      <span>Make people think about your brand</span>
                    </div>{' '}
                  </>
                ) : (
                  <>
                    <span className={'object-scale-down h-fit'}>
                      <ConversionIcon />
                    </span>
                    <div className={'flex flex-col gap-3'}>
                      <span className={'font-bold'}>Conversion</span>
                      <span>Get people wanting to see more</span>
                    </div>{' '}
                  </>
                )}
              </div>
            </div>
            <div className={'w-full py-8 border-b-2'}>
              <div className={'flex flex-row gap-3 items-center justify-between'}>
                <div className={'flex flex-row items-center gap-3'}>
                  <DollarSign />
                  <span className={'font-bold'}>Details</span>
                </div>
                <div
                  className={
                    'text-primary-persian-blue font-bold items-end self-end cursor-pointer'
                  }
                  onClick={() => setStep(4)}>
                  Edit
                </div>
              </div>
              <div className={'flex flex-row gap-5 w-full py-5'}>
                <div
                  className={
                    'p-5 flex flex-col gap-3 rounded-xl w-1/4 bg-secondary-sunglow/20'
                  }>
                  <span className={'text-sm'}>Location</span>
                  {formValues?.target?.zip?.split(',').map((zip) => (
                    <span key={zip} className={'font-bold text-md'}>
                      {zip}
                    </span>
                  ))}
                </div>
                <div
                  className={'p-5 flex flex-col gap-3 rounded-xl w-1/4 bg-blue-aero/20'}>
                  <span className={'text-sm'}>Dates</span>
                  <span className={'font-bold text-sm'}>
                    {dayjs(formValues?.start_date).format('MMM D')} -{' '}
                    {dayjs(formValues?.end_date).format('MMM D, YYYY')}
                  </span>
                </div>
              </div>
            </div>
            {/* Remove KPI details for now */}
            {/* <div className={'w-full py-8 border-b-2'}>
                  <div className={'flex flex-row gap-3 items-center justify-between'}>
                    <div className={'flex flex-row items-center gap-3'}>
                      <DollarSign />
                      <span className={'font-bold'}>KPI (Goals)</span>
                    </div>
                    <div
                      className={
                        'text-primary-persian-blue font-bold items-end self-end cursor-pointer'
                      }
                      onClick={() => setStep(4)}>
                      Edit
                    </div>
                  </div>

                  <div className={'flex flex-row gap-5 w-full py-5'}>
                    {formValues?.non_inventory?.kpi_goals?.primaryKpi && (
                      <div
                        className={
                          'p-5 flex flex-col gap-3 rounded-xl w-1/4 bg-primary-persian-blue/10'
                        }>
                        <span className={'text-sm'}>Primary KPI</span>
                        <span className={'font-bold text-md'}>
                          {formValues?.non_inventory?.kpi_goals?.primaryKpi}
                        </span>
                      </div>
                    )}
                    {formValues?.non_inventory?.kpi_goals?.secondaryKpi && (
                      <div
                        className={
                          'p-5 flex flex-col gap-3 rounded-xl w-1/4 bg-primary-persian-blue/10'
                        }>
                        <span className={'text-sm'}>Secondary KPI</span>
                        <span className={'font-bold text-md'}>
                          {formValues?.non_inventory?.kpi_goals?.secondaryKpi}
                        </span>
                      </div>
                    )}
                    {formValues?.non_inventory?.kpi_goals?.tertiaryKpi && (
                      <div
                        className={
                          'p-5 flex flex-col gap-3 rounded-xl w-1/4 bg-primary-persian-blue/10'
                        }>
                        <span className={'text-sm'}>Tertiary KPI</span>
                        <span className={'font-bold text-md'}>
                          {formValues?.non_inventory?.kpi_goals?.tertiaryKpi}
                        </span>
                      </div>
                    )}
                  </div>
                </div> */}
            {formValues?.target?.use_custom_audience && (
              <div className={'w-full py-8 border-b-2 flex flex-col gap-5'}>
                <div className={'flex flex-row gap-3 items-center justify-between'}>
                  <div className={'flex flex-row items-center gap-3'}>
                    <PeopleIcon />
                    <span className={'font-bold'}>Marketing audience</span>
                  </div>
                  <div
                    className={
                      'text-primary-persian-blue font-bold items-end self-end cursor-pointer'
                    }
                    onClick={() => setStep(5)}>
                    Edit
                  </div>
                </div>
                <div className={'grid grid-cols-4 gap-5 items-center'}>
                  {take(
                    formValues?.target?.audience.map((audience) => (
                      <div
                        key={audience.name}
                        className={
                          'flex flex-col gap-3 border-2 rounded-lg h-full col-span-1 items-center p-5'
                        }>
                        <span className={'text-xs text-center'}>{audience.name}</span>
                        <span className={'text-sm font-bold text-center'}>
                          Audience size: {formatNumber(Number(audience.size))}
                        </span>
                      </div>
                    )),
                    3,
                  )}
                </div>
              </div>
            )}

            <div className={'w-full py-8 border-b-2'}>
              <div className={'flex flex-row gap-3 items-center justify-between'}>
                <div className={'flex flex-row items-center gap-3'}>
                  <AnnouncementIcon />
                  <span className={'font-bold'}>Promote using</span>
                </div>
              </div>

              <div className={'flex flex-row gap-5 w-full py-5'}>
                <div
                  className={
                    'flex flex-col items-center border-2 p-4 rounded-lg w-1/4 gap-3 text-center'
                  }>
                  {formValues.product?.icon}
                  <span className={'font-bold'}>{formValues.product?.product_name}</span>
                  <span className={'text-xs'}>
                    {formValues.product?.product_description}
                  </span>
                  <span className={'text-xs font-bold'}>
                    Budget: ${formatNumber(Number(formValues?.budget))}
                  </span>
                </div>
              </div>
            </div>

            {formValues.product?.creatives &&
              formValues.product?.creatives.length > 0 && (
                <div className={'w-full py-8 border-b-2'}>
                  <div className={'flex flex-row gap-3 items-center justify-between'}>
                    <div className={'flex flex-row items-center gap-3 py-5'}>
                      <PeopleIcon />
                      <span className={'font-bold'}>
                        {formValues.product?.product_name} Custom Creatives
                      </span>
                    </div>
                  </div>
                  <div
                    className={`flex flex-row flex-nowrap gap-4 items-center rounded-lg text-center`}>
                    {formValues.product.creatives.map((creative: string, i) => {
                      return (
                        <div
                          key={creative}
                          className={
                            'w-full object-scale-down border-2 p-3 flex flex-col gap-3 items-center rounded-lg text-center text-sm font-bold h-full justify-between col-span-1'
                          }>
                          {formValues.product?.product_code === 'mob' ? (
                            <img src={creative} />
                          ) : formValues.product?.product_code === 'aud' ? (
                            <HoverMedia src={creative} type={'audio'} />
                          ) : (
                            <HoverMedia
                              src={creative}
                              type={'video'}
                              className={`rounded-xl w-fit h-fit object-cover object-scale-down max-h-40`}
                            />
                          )}
                          <span>Creative #{i + 1}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            <div className={'w-full py-8 border-b-2'}>
              <div className={'flex flex-row gap-3 items-center justify-between'}>
                <div className={'flex flex-row items-center gap-3'}>
                  <PeopleIcon />
                  <span className={'font-bold'}>Additional Notes</span>
                </div>
              </div>

              <div className={'flex flex-row gap-5 w-full py-5'}>
                <TextArea inverted={true} label={''} name={`notes`} />
              </div>
            </div>
          </div>
        )}
      </motion.div>
      <motion.div
        layout
        layoutId={'buttons-container'}
        className={
          'h-20 items-center flex justify-between border-t-2 py-4 px-12 absolute bottom-0 w-full'
        }>
        <Button
          variant={'secondary'}
          onClick={(e) => {
            e.preventDefault()
            setStep(step - 1)
          }}>
          Back
        </Button>
        <CampaignProgressBar step={step} />
        <Button variant={'primary'} disabled={isLoading}>
          Create campaign!
        </Button>
      </motion.div>
    </motion.form>
  )
}

export default CreateNonInventoryCampaignReview
